import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import GlowButton from '../components/ui/GlowButton';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import api from '../services/api';
import './Pricing.css';

const DEFAULT_PLANS = {
  free: {
    id: 'free',
    nameKey: 'pricing.free',
    defaultName: 'Gratis',
    price: 0,
    features: {
      maxApiKeys: 1,
      maxPlaylists: 1,
      maxPlaylistItems: 20,
      allowInteractivity: false,
      allowCustomIntros: false,
      allowEffects: false,
    },
  },
  pro: {
    id: 'pro',
    nameKey: 'pricing.pro',
    defaultName: 'Pro',
    price: parseInt(import.meta.env.VITE_PLAN_PRO_PRICE || '4990'),
    features: {
      maxApiKeys: 3,
      maxPlaylists: -1,
      maxPlaylistItems: -1,
      allowInteractivity: true,
      allowCustomIntros: true,
      allowEffects: true,
    },
  },
  premium: {
    id: 'premium',
    nameKey: 'pricing.premium',
    defaultName: 'Premium',
    price: parseInt(import.meta.env.VITE_PLAN_PREMIUM_PRICE || '9990'),
    features: {
      maxApiKeys: 10,
      maxPlaylists: -1,
      maxPlaylistItems: -1,
      allowInteractivity: true,
      allowCustomIntros: true,
      allowEffects: true,
    },
  },
};

export default function Pricing() {
  const { t } = useTranslation();
  const { user, selectPlan, currentPlan, DEV_MODE } = useAuth();
  const navigate = useNavigate();

  const [processors, setProcessors] = useState(['mercadopago']);
  const [selectedProcessor, setSelectedProcessor] = useState('mercadopago');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const envProcessors = (import.meta.env.VITE_PAYMENT_PROCESSORS || 'mercadopago')
      .split(',')
      .map((p) => p.trim());
    setProcessors(envProcessors);
    setSelectedProcessor(envProcessors[0] || 'mercadopago');

    // Consultar procesadores del backend
    api
      .getPaymentProcessors()
      .then((res) => {
        if (res.data?.processors) {
          setProcessors(res.data.processors);
          if (res.data.default) setSelectedProcessor(res.data.default);
        }
      })
      .catch((err) => console.log('Usando procesadores locales:', err.message));
  }, []);

  const handleChoosePlan = async (planId) => {
    setErrorMsg('');

    if (!user) {
      navigate(`/register?returnTo=/pricing`);
      return;
    }

    if (planId === 'free') {
      setLoadingPlan('free');
      try {
        await api.activateFreePlan().catch(() => {});
        selectPlan('free');
        navigate('/dashboard');
      } catch (err) {
        selectPlan('free');
        navigate('/dashboard');
      } finally {
        setLoadingPlan(null);
      }
      return;
    }

    // Planes pagos (Pro / Premium)
    setLoadingPlan(planId);
    try {
      const res = await api.createCheckout({
        plan: planId,
        processor: selectedProcessor,
        cycle: billingCycle,
      });

      if (res.data?.url) {
        // Redirigir a pasarela (MercadoPago / Stripe)
        window.location.href = res.data.url;
      } else {
        // Modo dev o respuesta directa
        selectPlan(planId);
        navigate('/dashboard');
      }
    } catch (err) {
      console.warn('Checkout backend no disponible o error:', err.message);
      if (DEV_MODE) {
        // En modo dev activar directamente para pruebas
        selectPlan(planId);
        navigate('/dashboard');
      } else {
        setErrorMsg(err.response?.data?.error || err.message || 'Error al iniciar pago');
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  const planCards = Object.entries(DEFAULT_PLANS).map(([key, plan]) => ({
    id: key,
    ...plan,
    isPopular: key === 'pro',
  }));

  return (
    <div className="pricing-page" id="pricing-page">
      <header
        className="pricing-page__header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.25rem 2rem',
        }}
      >
        <button className="pricing-page__back" onClick={() => navigate(-1)}>
          ← {t('common.back', 'Volver')}
        </button>
        <LanguageSwitcher />
      </header>

      <div className="pricing-page__content">
        <h1 className="pricing-page__title text-gradient">{t('pricing.title', 'Elegí tu plan')}</h1>
        <p className="pricing-page__subtitle">
          {t(
            'pricing.subtitle',
            'Todos los planes incluyen acceso al karaoke online. Actualizá cuando quieras.'
          )}
        </p>

        {errorMsg && (
          <div
            style={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '0.75rem 1.5rem',
              color: '#fca5a5',
              maxWidth: '600px',
              margin: '0 auto 2rem auto',
              textAlign: 'center',
            }}
          >
            {errorMsg}
          </div>
        )}

        {/* Ciclo de Facturación (Mensual / Anual) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '2rem',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '9999px',
              padding: '0.3rem',
            }}
          >
            <button
              onClick={() => setBillingCycle('monthly')}
              style={{
                background: billingCycle === 'monthly' ? 'linear-gradient(135deg, #ff2d9b, #b44dff)' : 'transparent',
                border: 'none',
                color: '#ffffff',
                padding: '0.45rem 1.2rem',
                borderRadius: '9999px',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              style={{
                background: billingCycle === 'annual' ? 'linear-gradient(135deg, #ff2d9b, #b44dff)' : 'transparent',
                border: 'none',
                color: '#ffffff',
                padding: '0.45rem 1.2rem',
                borderRadius: '9999px',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Anual <span style={{ color: '#34d399', fontSize: '0.78rem' }}>(-20% OFF)</span>
            </button>
          </div>
        </div>

        {/* Selector de Procesador si hay más de 1 */}
        {processors.length > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '2.5rem',
            }}
          >
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>Pagar con:</span>
            {processors.map((proc) => (
              <button
                key={proc}
                onClick={() => setSelectedProcessor(proc)}
                style={{
                  background: selectedProcessor === proc ? 'rgba(255, 45, 155, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                  border: `1px solid ${selectedProcessor === proc ? '#ff2d9b' : 'rgba(255,255,255,0.1)'}`,
                  color: selectedProcessor === proc ? '#ffffff' : 'rgba(255,255,255,0.6)',
                  padding: '0.35rem 0.9rem',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {proc === 'mercadopago' ? '💳 MercadoPago' : proc === 'stripe' ? '🌐 Stripe' : proc}
              </button>
            ))}
          </div>
        )}

        <div className="pricing-page__cards">
          {planCards.map((plan) => {
            const isCurrent = currentPlan() === plan.id;
            const finalPrice =
              billingCycle === 'annual' && plan.price > 0
                ? Math.round(plan.price * 0.8)
                : plan.price;

            return (
              <div
                key={plan.id}
                className={`pricing-card glass-panel ${
                  plan.isPopular ? 'pricing-card--popular' : ''
                } ${isCurrent ? 'pricing-card--current' : ''}`}
              >
                {plan.isPopular && <div className="pricing-card__badge">⭐ Popular</div>}
                <h2 className="pricing-card__name">{t(plan.nameKey, plan.defaultName)}</h2>
                <div className="pricing-card__price">
                  {plan.price === 0 ? (
                    <span className="pricing-card__amount">{t('pricing.free', 'Gratis')}</span>
                  ) : (
                    <>
                      <span className="pricing-card__currency">$</span>
                      <span className="pricing-card__amount">{finalPrice.toLocaleString()}</span>
                      <span className="pricing-card__period">{t('pricing.monthly', '/ mes')}</span>
                    </>
                  )}
                </div>
                <ul className="pricing-card__features">
                  <li>🔑 {plan.features.maxApiKeys} API Keys</li>
                  <li>🎵 {plan.features.maxPlaylists === -1 ? '∞' : plan.features.maxPlaylists} Playlists</li>
                  <li>
                    📋 {plan.features.maxPlaylistItems === -1 ? '∞' : plan.features.maxPlaylistItems}{' '}
                    canciones/lista
                  </li>
                  <li>
                    🎤{' '}
                    {plan.features.allowInteractivity
                      ? '✅ Interactividad celular'
                      : '❌ Sin interactividad'}
                  </li>
                  <li>
                    🎬{' '}
                    {plan.features.allowCustomIntros
                      ? '✅ Intros y transiciones'
                      : '❌ Sin intros'}
                  </li>
                  <li>
                    🔊{' '}
                    {plan.features.allowEffects ? '✅ Consola de efectos' : '❌ Sin efectos'}
                  </li>
                </ul>

                <GlowButton
                  variant={plan.isPopular ? 'primary' : 'secondary'}
                  size="md"
                  onClick={() => handleChoosePlan(plan.id)}
                  className="pricing-card__btn"
                  disabled={loadingPlan === plan.id}
                >
                  {loadingPlan === plan.id
                    ? 'Procesando...'
                    : isCurrent
                    ? t('pricing.currentPlan', 'Plan actual')
                    : t('pricing.choosePlan', 'Elegir plan')}
                </GlowButton>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
